<template>
  <div class="mfa-enrollment">
    <v-dialog
      v-model="dialog"
      max-width="800px"
      persistent
      :fullscreen="$vuetify.breakpoint.xs"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          color="primary"
          depressed
          text
          v-bind="attrs"
          v-on="on"
          :loading="loading"
        >
          <v-icon left>mdi-shield-lock</v-icon>
          Zwei-Faktor-Authentifizierung aktivieren
        </v-btn>
      </template>

      <v-card>
        <v-card-title>
          <span class="text-h5">Zwei-Faktor-Authentifizierung einrichten</span>
        </v-card-title>

        <v-card-text>
          <v-stepper v-model="step" vertical flat>
            <!-- Step 1: Generate Enrollment -->
            <v-stepper-step :complete="step > 1" step="1">
              Authenticator-App konfigurieren
            </v-stepper-step>
            <v-stepper-content step="1">
              <div v-if="!enrollmentStarted">
                <p class="mb-2">Bitte bestätige zuerst Deine Identität:</p>
                <v-text-field
                  v-model="password"
                  label="Passwort"
                  type="password"
                  outlined
                  dense
                  @keyup.enter="startEnrollment"
                ></v-text-field>
                <v-alert v-if="errorMsg" type="error" class="mb-4" text>
                  {{ errorMsg }}
                </v-alert>
                <v-btn
                  color="primary"
                  @click="startEnrollment"
                  :loading="loading"
                  :disabled="!password"
                  depressed
                >
                  Einrichtung starten
                </v-btn>
              </div>

              <div v-else>
                <v-alert type="info" text class="mb-4">
                  Du benötigst eine Authenticator-App wie Google Authenticator
                  Microsoft Authenticator oder Authy auf Deinem Smartphone.
                </v-alert>
                <v-card
                  v-if="qrCodeUrl && !showSecretKey"
                  outlined
                  class="justify-center text-center"
                >
                  <v-card-text>
                    <div>
                      <p>Scanne diesen QR-Code mit Deiner Authenticator-App:</p>
                      <img
                        :src="qrCodeUrl"
                        alt="QR Code"
                        style="width: 200px; height: 200px"
                      />
                    </div>

                    <v-btn text small @click="showSecretKey = !showSecretKey">
                      <v-icon left small>mdi-key-variant</v-icon>
                      {{
                        showSecretKey
                          ? "Code ausblenden"
                          : "QR-Code nicht scanbar? Code manuell eingeben"
                      }}
                    </v-btn>
                  </v-card-text>
                </v-card>
                <v-card v-else outlined>
                  <v-card-text>
                    <p>
                      Gib diesen Code manuell in Deine Authenticator-App ein:
                    </p>
                    <div class="d-flex flex-wrap align-center mt-n3">
                      <code
                        class="text-body-2 mt-2 mr-2"
                        style="
                          font-family: monospace;
                          letter-spacing: 0.08em;
                          word-break: break-all;
                        "
                        >{{ totpSecret ? totpSecret.secretKey : "" }}</code
                      >
                      <v-btn
                        depressed
                        small
                        class="mt-2"
                        @click="copySecretKey"
                      >
                        <v-icon small left>{{
                          copied ? "mdi-check" : "mdi-content-copy"
                        }}</v-icon
                        >{{
                          copied ? "Kopiert!" : "In die Zwischenablage kopieren"
                        }}
                      </v-btn>
                    </div>
                    <v-btn
                      text
                      small
                      class="mt-3"
                      @click="showSecretKey = !showSecretKey"
                    >
                      <v-icon left small>mdi-qrcode-scan</v-icon>
                      QR-Code anzeigen
                    </v-btn>
                  </v-card-text>
                </v-card>

                <v-alert v-if="errorMsg" type="error" class="mt-4" text>
                  {{ errorMsg }}
                </v-alert>
                <v-btn color="primary" depressed @click="step = 2" class="mt-4">
                  Weiter
                </v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 2: Verify Code -->
            <v-stepper-step :complete="step > 2" step="2">
              Bestätigungscode eingeben
            </v-stepper-step>
            <v-stepper-content step="2">
              <p class="mb-4">
                Gib den 6-stelligen Code ein, der in Deiner Authenticator-App
                angezeigt wird:
              </p>
              <v-text-field
                v-model="verificationCode"
                label="Bestätigungscode"
                outlined
                maxlength="6"
                :rules="[rules.required, rules.sixDigits]"
                @keyup.enter="verifyEnrollment"
              ></v-text-field>
              <v-alert v-if="errorMsg" type="error" class="mb-4" text>
                {{ errorMsg }}
              </v-alert>
              <div class="d-flex">
                <v-btn text @click="step = 1">Zurück</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  @click="verifyEnrollment"
                  :loading="loading"
                  depressed
                  :disabled="!verificationCode || verificationCode.length !== 6"
                >
                  Verifizieren
                </v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 3: Success -->
            <v-stepper-step :complete="step > 3" step="3">
              Erfolgreich eingerichtet
            </v-stepper-step>
            <v-stepper-content step="3">
              <v-alert type="success" class="mb-4" text icon="mdi-shield-check">
                Zwei-Faktor-Authentifizierung wurde erfolgreich aktiviert!
              </v-alert>
              <p class="mb-4">
                Ab sofort wird bei der Anmeldung ein Code aus Deiner
                Authenticator-App benötigt.
              </p>
              <v-btn color="primary" depressed @click="finishEnrollment">
                Fertigstellen
              </v-btn>
            </v-stepper-content>
          </v-stepper>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog" v-if="step !== 3">Abbrechen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as fb from "@/firebase";
import { TotpMultiFactorGenerator } from "firebase/auth";
import { handleAuthError } from "@/util/firebase/FirebaseErrorHandling.js";
import QRCode from "qrcode";

export default {
  name: "MFAEnrollment",
  data() {
    return {
      dialog: false,
      step: 1,
      loading: false,
      enrollmentStarted: false,
      qrCodeUrl: null,
      verificationCode: "",
      password: "",
      errorMsg: "",
      showSecretKey: false,
      copied: false,
      multiFactorSession: null,
      totpSecret: null,
      rules: {
        required: (value) => !!value || "Erforderlich",
        sixDigits: (value) =>
          /^\d{6}$/.test(value) || "Muss 6 Ziffern enthalten",
      },
    };
  },
  methods: {
    async startEnrollment() {
      this.loading = true;
      this.errorMsg = "";
      try {
        const user = fb.auth.currentUser;

        // Re-authenticate before sensitive MFA operation
        const credential = fb.EmailAuthProvider.credential(
          user.email,
          this.password,
        );
        await user.reauthenticateWithCredential(credential);

        // Get multi-factor session
        const multiFactorSession =
          await fb.auth.currentUser.multiFactor.getSession();
        this.multiFactorSession = multiFactorSession;

        // Generate TOTP secret
        const totpSecret = await TotpMultiFactorGenerator.generateSecret(
          multiFactorSession,
        );
        this.totpSecret = totpSecret;

        // Generate QR code image from the otpauth:// URI
        const otpauthUrl = totpSecret.generateQrCodeUrl(user.email, "Mira One");
        this.qrCodeUrl = await QRCode.toDataURL(otpauthUrl, {
          width: 250,
          margin: 2,
        });

        this.enrollmentStarted = true;
      } catch (error) {
        this.errorMsg = handleAuthError(error.code);
        console.error("Error starting enrollment:", error);
      } finally {
        this.loading = false;
      }
    },

    async verifyEnrollment() {
      this.loading = true;
      this.errorMsg = "";
      try {
        // Create TOTP assertion from the verification code
        const multiFactorAssertion =
          TotpMultiFactorGenerator.assertionForEnrollment(
            this.totpSecret,
            this.verificationCode,
          );

        // Enroll the second factor
        await fb.auth.currentUser.multiFactor.enroll(
          multiFactorAssertion,
          "Authenticator App",
        );

        this.step = 3;
      } catch (error) {
        this.errorMsg = handleAuthError(error.code);
        console.error("Error verifying enrollment:", error);
      } finally {
        this.loading = false;
      }
    },

    async copySecretKey() {
      if (!this.totpSecret) return;
      try {
        await navigator.clipboard.writeText(this.totpSecret.secretKey);
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
      } catch (e) {
        console.error("Copy failed", e);
      }
    },

    finishEnrollment() {
      this.$emit("enrollment-complete");
      this.closeDialog();
      this.$emit("refresh");
    },

    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },

    resetForm() {
      this.step = 1;
      this.enrollmentStarted = false;
      this.qrCodeUrl = null;
      this.verificationCode = "";
      this.password = "";
      this.errorMsg = "";
      this.showSecretKey = false;
      this.copied = false;
      this.multiFactorSession = null;
      this.totpSecret = null;
    },
  },
};
</script>
